import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  listPendingRiders,
  listApprovedRiders,
  getAdminRider,
  setRiderApproval,
  toggleRiderBlock,
  setRiderPriority,
  getProfileChangeRequests,
  decideProfileChangeRequest,
  getAdminFleetOwners,
  assignRiderFleetOwner,
  adminResetRiderPassword
} from "../../api/client";
import ResetPasswordModal from "../../components/ResetPasswordModal";
import AdminNav from "../../components/AdminNav";
import LoadingIndicator from "../../components/LoadingIndicator";
import FullDispatcherModal from "../../components/FullDispatcherModal";

// Collapsible section — used inside the change request diff modal
function Collapsible({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="quote-box" style={{ marginTop: "12px", padding: 0, overflow: "hidden" }}>
      <button
        type="button"
        className="secondary"
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", margin: 0, borderRadius: 0, border: "none", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "0 16px 16px" }}>{children}</div>}
    </div>
  );
}

// Rendered via a portal straight into <body>
function Modal({ onClose, maxWidth = "560px", children }) {
  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}
      onClick={onClose}
    >
      <div
        className="delivery-card"
        style={{ maxWidth, width: "100%", margin: 0, maxHeight: "calc(100vh - 40px)", overflowY: "auto", WebkitOverflowScrolling: "touch", boxSizing: "border-box" }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function RiderApproval() {
  const { id: routeRiderId } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState("pending"); // "pending" | "approved" | "changes"
  const [pendingRiders, setPendingRiders] = useState([]);
  const [approvedRiders, setApprovedRiders] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [blockReasonMap, setBlockReasonMap] = useState({});
  const [resetPasswordRider, setResetPasswordRider] = useState(null); // full rider object
  const [search, setSearch] = useState("");

  // Which dispatcher's full-detail dossier modal is open
  const [viewRider, setViewRider] = useState(null);
  const [viewChangeRider, setViewChangeRider] = useState(null);

  const [fleetOwners, setFleetOwners] = useState([]);
  const [fleetBusyId, setFleetBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [pRes, aRes, cRes, fRes] = await Promise.all([
        listPendingRiders(),
        listApprovedRiders(),
        getProfileChangeRequests(),
        getAdminFleetOwners("approved")
      ]);
      setPendingRiders(pRes.data);
      setApprovedRiders(aRes.data);
      setChangeRequests(cRes.data);
      setFleetOwners(fRes.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load riders list");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChangeDecision = async (riderId, approve) => {
    setBusyId(riderId);
    setError("");
    try {
      await decideProfileChangeRequest(riderId, approve);
      await load();
      setViewChangeRider(null);
    } catch (err) {
      setError(err.response?.data?.error || "Could not process change request");
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // If a specific rider ID was passed in URL /admin/riders/:id, fetch & open their full dossier
  useEffect(() => {
    if (routeRiderId) {
      getAdminRider(routeRiderId)
        .then((res) => {
          setViewRider(res.data);
          if (res.data?.approvalStatus === "approved") setTab("approved");
          else if (res.data?.approvalStatus === "pending") setTab("pending");
        })
        .catch(() => {});
    }
  }, [routeRiderId]);

  const handleCloseViewRider = () => {
    setViewRider(null);
    if (routeRiderId) {
      navigate("/admin/riders", { replace: true });
    }
  };

  // Keep the open modal's data in sync after every reload
  useEffect(() => {
    if (viewRider) {
      const fresh = [...pendingRiders, ...approvedRiders].find((r) => r._id === viewRider._id);
      if (fresh) {
        setViewRider((prev) => ({ ...prev, ...fresh }));
      }
    }
  }, [pendingRiders, approvedRiders]);

  useEffect(() => {
    if (viewChangeRider) {
      const fresh = changeRequests.find((r) => r._id === viewChangeRider._id);
      setViewChangeRider(fresh || null);
    }
  }, [changeRequests]);

  const handleDecision = async (riderId, status) => {
    setBusyId(riderId);
    setError("");
    try {
      await setRiderApproval(riderId, status);
      await load();
      if (status !== "pending") handleCloseViewRider();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update rider status");
    } finally {
      setBusyId(null);
    }
  };

  const handleBlockToggle = async (rider, targetBlockState) => {
    setBusyId(rider._id);
    setError("");
    try {
      const reason = blockReasonMap[rider._id] || (targetBlockState ? "Negative wallet balance / platform policy violation" : "");
      await toggleRiderBlock(rider._id, targetBlockState, reason);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update rider block status");
    } finally {
      setBusyId(null);
    }
  };

  const handlePriorityToggle = async (rider) => {
    setBusyId(rider._id);
    setError("");
    try {
      const next = rider.priorityStatus === "priority" ? "regular" : "priority";
      await setRiderPriority(rider._id, next);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update priority status");
    } finally {
      setBusyId(null);
    }
  };

  const handleAssignFleetOwner = async (rider, fleetOwnerId) => {
    setFleetBusyId(rider._id);
    setError("");
    try {
      await assignRiderFleetOwner(rider._id, fleetOwnerId || null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update fleet assignment");
    } finally {
      setFleetBusyId(null);
    }
  };

  const matchesSearch = (rider) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      rider.name?.toLowerCase().includes(q) ||
      rider.phone?.toLowerCase().includes(q) ||
      rider.email?.toLowerCase().includes(q) ||
      rider.city?.toLowerCase().includes(q)
    );
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

      <div className="brand-header">
        <span className="brand-logo">🛵</span>
        <span className="brand-name">Dispatcher Fleet &amp; Approvals</span>
        <p className="brand-tagline">Manage applications, online fleet, wallet balances, and account suspensions</p>
      </div>

      {/* TABS HEADER */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          className={tab === "pending" ? "" : "secondary"}
          style={{ width: "auto", padding: "8px 16px", marginTop: 0 }}
          onClick={() => setTab("pending")}
        >
          ⌛ Pending Applications ({pendingRiders.length})
        </button>
        <button
          className={tab === "approved" ? "" : "secondary"}
          style={{ width: "auto", padding: "8px 16px", marginTop: 0 }}
          onClick={() => setTab("approved")}
        >
          👥 Approved Fleet &amp; Wallets ({approvedRiders.length})
        </button>
        <button
          className={tab === "changes" ? "" : "secondary"}
          style={{ width: "auto", padding: "8px 16px", marginTop: 0 }}
          onClick={() => setTab("changes")}
        >
          ✏️ Profile Change Requests ({changeRequests.length})
        </button>
      </div>

      {/* SEARCH — filters whichever tab is active, by name/phone/email/city */}
      <input
        type="text"
        placeholder="Search by name, phone, email, or city..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: "20px" }}
      />

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading fleet data..." />
      ) : tab === "pending" ? (
        /* PENDING APPLICATIONS TAB — compact list; click a card for the full KYC */
        pendingRiders.filter(matchesSearch).length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">✅</span>
            <p className="empty-state-text">
              {pendingRiders.length === 0 ? "No pending dispatcher applications right now." : "No dispatchers match your search."}
            </p>
          </div>
        ) : (
          pendingRiders.filter(matchesSearch).map((rider) => (
            <div
              key={rider._id}
              className="rider-card"
              style={{ marginBottom: "12px", cursor: "pointer" }}
              onClick={() => setViewRider(rider)}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "16px" }}>{rider.name}</strong>
                  <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                    {rider.phone} · {rider.vehicleType?.toUpperCase()} · {rider.city || "Lagos"}
                    {rider.kyc?.nin ? ` · NIN: ${rider.kyc.nin}` : ""}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span className="status-badge warning">Pending Approval</span>
                  <span style={{ color: "var(--text-muted)" }}>Full Details →</span>
                </div>
              </div>
            </div>
          ))
        )
      ) : tab === "approved" ? (
        /* APPROVED FLEET & WALLETS TAB — compact list; click a card for full details */
        approvedRiders.filter(matchesSearch).length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">🛵</span>
            <p className="empty-state-text">
              {approvedRiders.length === 0 ? "No approved dispatchers found." : "No dispatchers match your search."}
            </p>
          </div>
        ) : (
          approvedRiders.filter(matchesSearch).map((rider) => {
            const net = rider.wallet?.netBalance ?? 0;
            const isNegative = net < 0;
            return (
              <div
                key={rider._id}
                className="rider-card"
                style={{ marginBottom: "12px", borderColor: rider.isBlocked ? "var(--danger)" : undefined, cursor: "pointer" }}
                onClick={() => setViewRider(rider)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <strong style={{ fontSize: "16px" }}>{rider.name}</strong>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                      <span style={{ color: rider.isOnline ? "var(--text-accent)" : "var(--text-muted)" }}>{rider.isOnline ? "🟢 Online" : "⚫ Offline"}</span>
                      {" · ⭐ "}{rider.rating?.toFixed(1) || "5.0"}
                      {" · "}{rider.vehicleType?.toUpperCase()} · {rider.city}
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {rider.priorityStatus === "priority" && <span className="status-badge info">⚡ PRIORITY</span>}
                      <span className={`status-badge ${rider.isBlocked ? "danger" : "info"}`}>
                        {rider.isBlocked ? "🚫 SUSPENDED" : "APPROVED"}
                      </span>
                    </div>
                    <strong style={{ fontSize: "15px", color: isNegative ? "var(--danger)" : "var(--text-accent)" }}>
                      {isNegative ? `-₦${Math.abs(net).toLocaleString()}` : `₦${net.toLocaleString()}`}
                    </strong>
                  </div>
                </div>
              </div>
            );
          })
        )
      ) : (
        /* PROFILE CHANGE REQUESTS TAB — compact list; click a card for the full diff */
        changeRequests.filter(matchesSearch).length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">✅</span>
            <p className="empty-state-text">
              {changeRequests.length === 0 ? "No pending profile change requests." : "No dispatchers match your search."}
            </p>
          </div>
        ) : (
          changeRequests.filter(matchesSearch).map((rider) => (
            <div
              key={rider._id}
              className="rider-card"
              style={{ marginBottom: "12px", cursor: "pointer" }}
              onClick={() => setViewChangeRider(rider)}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "16px" }}>{rider.name}</strong>
                  <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                    Requested {new Date(rider.profileChangeRequest?.requestedAt).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span className="status-badge warning">Pending Review</span>
                  <span style={{ color: "var(--text-muted)" }}>Details →</span>
                </div>
              </div>
            </div>
          ))
        )
      )}

      {/* PROFILE CHANGE REQUEST — FULL DIFF MODAL */}
      {viewChangeRider && (
        <Modal onClose={() => setViewChangeRider(null)} maxWidth="640px">
          {(() => {
            const rider = viewChangeRider;
            const kycChanges = rider.profileChangeRequest?.requestedChanges?.kyc;
            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{rider.name}</h3>
                    <p className="hint" style={{ marginTop: "4px" }}>
                      Requested {new Date(rider.profileChangeRequest?.requestedAt).toLocaleString()}
                    </p>
                  </div>
                  <button className="secondary" style={{ width: "auto", padding: "4px 12px", marginTop: 0 }} onClick={() => setViewChangeRider(null)}>✕ Close</button>
                </div>

                <div className="info-row">
                  <span className="label">Current</span>
                  <span className="value">{rider.name} · {rider.phone} · {rider.vehicleType?.toUpperCase()} ({rider.vehiclePlateNumber || "No Plate"})</span>
                </div>
                <div className="info-row">
                  <span className="label">Requested</span>
                  <span className="value" style={{ color: "var(--text-accent)" }}>
                    {rider.profileChangeRequest?.requestedChanges?.name && `Name → ${rider.profileChangeRequest.requestedChanges.name}. `}
                    {rider.profileChangeRequest?.requestedChanges?.phone && `Phone → ${rider.profileChangeRequest.requestedChanges.phone}. `}
                    {rider.profileChangeRequest?.requestedChanges?.vehicleType && `Vehicle → ${rider.profileChangeRequest.requestedChanges.vehicleType}. `}
                    {rider.profileChangeRequest?.requestedChanges?.vehiclePlateNumber && `Plate → ${rider.profileChangeRequest.requestedChanges.vehiclePlateNumber}.`}
                  </span>
                </div>

                {kycChanges && Object.keys(kycChanges).length > 0 && (
                  <Collapsible title="🪪 Requested KYC Changes" defaultOpen>
                    {Object.entries(kycChanges).map(([field, value]) => {
                      if (field === "guarantor") {
                        return Object.entries(value || {}).map(([gField, gValue]) => (
                          <div className="info-row" key={`guarantor.${gField}`}>
                            <span className="label">Guarantor {gField}</span>
                            <span className="value" style={{ color: "var(--text-accent)" }}>{String(gValue)}</span>
                          </div>
                        ));
                      }
                      return (
                        <div className="info-row" key={field}>
                          <span className="label">{field.replace(/([A-Z])/g, " $1")}</span>
                          <span className="value" style={{ color: "var(--text-accent)" }}>
                            {field === "hasValidLasdri" ? (value ? "Yes" : "No") : field === "dateOfBirth" ? new Date(value).toLocaleDateString() : String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </Collapsible>
                )}

                <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                  <button
                    style={{ width: "auto", padding: "6px 16px", marginTop: 0 }}
                    disabled={busyId === rider._id}
                    onClick={() => handleProfileChangeDecision(rider._id, true)}
                  >
                    Approve Changes
                  </button>
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "6px 16px", marginTop: 0, color: "var(--danger-hover)" }}
                    disabled={busyId === rider._id}
                    onClick={() => handleProfileChangeDecision(rider._id, false)}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    style={{ width: "auto", padding: "6px 16px", marginTop: 0 }}
                    onClick={() => setViewRider(rider)}
                  >
                    👤 Full Dispatcher Dossier →
                  </button>
                </div>
              </>
            );
          })()}
        </Modal>
      )}

      {/* FULL DISPATCHER DETAILS DOSSIER MODAL */}
      {viewRider && (
        <FullDispatcherModal
          rider={viewRider}
          onClose={handleCloseViewRider}
          fleetOwners={fleetOwners}
          busyId={busyId}
          fleetBusyId={fleetBusyId}
          blockReasonMap={blockReasonMap}
          setBlockReasonMap={setBlockReasonMap}
          onDecision={handleDecision}
          onBlockToggle={handleBlockToggle}
          onPriorityToggle={handlePriorityToggle}
          onAssignFleetOwner={handleAssignFleetOwner}
          onResetPassword={(r) => setResetPasswordRider(r)}
        />
      )}

      {resetPasswordRider && (
        <ResetPasswordModal
          name={resetPasswordRider.name}
          onSubmit={(newPassword) => adminResetRiderPassword(resetPasswordRider._id, newPassword)}
          onClose={() => setResetPasswordRider(null)}
        />
      )}
      </div>
    </div>
  );
}
